import React from 'react';
import { signupStyles } from '@/components/features/registration/signup-templates/signup.styles';
import {
   Box,
   Stack,
   Button,
   Field,
   Input,
   IconButton,
   Select,
   Portal,
   createListCollection,
   Text,
   InputGroup,
} from '@chakra-ui/react';
import PhoneInput from 'react-phone-input-2';
import { partnerDetailsSchema, SOCIAL_MEDIA_VALUES, TPartnerDetails } from './partner-details.validator';
import { useFieldArray, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import ReactFlagsSelect from 'react-flags-select';
import { FaMinus, FaPlus } from 'react-icons/fa';
import { createPartnerDetails } from '@/services/partner/partner.service';
import useAuthFunc from '@/hooks/auth/useAuthFunc';

const PartnerDetailsForm = () => {
   const { redirect, refreshSession } = useAuthFunc();
   const socialMedia = createListCollection({
      items: SOCIAL_MEDIA_VALUES.map(item => ({
         label: item,
         value: item,
      })),
   });
   const {
      register,
      handleSubmit,
      reset,
      setValue,
      watch,
      control,
      formState: { errors },
   } = useForm<TPartnerDetails>({
      defaultValues: {
         social_media: [
            {
               platform: '',
               url: '',
            },
         ],
      },
      resolver: zodResolver(partnerDetailsSchema),
   });

   const { fields, append, remove } = useFieldArray<TPartnerDetails>({
      control,
      name: 'social_media', // name must match the defaultValues structure
   });

   const onSubmit = handleSubmit(async value => {
      const [error, data] = await createPartnerDetails(value);

      if (error) {
         console.log(error);
         return;
      }

      await refreshSession();

      redirect(data?.response?.registration_step);
      return;
   });

   const { phone_number, nationality } = watch();

   return (
      <Box as="form" css={signupStyles.signup_form} onSubmit={onSubmit}>
         <Stack gap="4" align="flex-start" minW="full">
            <Field.Root invalid={Boolean(errors.address_1?.message)}>
               <Field.Label textStyle="xs" color="white" mb="1">
                  Address Line 1
                  <Box as="span" color="error">
                     *
                  </Box>
               </Field.Label>
               <Input
                  placeholder="Address Line 1"
                  variant="subtle"
                  h="10"
                  rounded="12px"
                  bg="#202020"
                  _focusVisible={{
                     border: 'none',
                     outline: 'none',
                     boxShadow: 'none',
                  }}
                  {...register('address_1')}
               />
               <Field.ErrorText>{errors.address_1?.message}</Field.ErrorText>
            </Field.Root>
            <Field.Root invalid={Boolean(errors.address_2?.message)}>
               <Field.Label textStyle="xs" color="white" mb="1">
                  Address Line 2
               </Field.Label>
               <Input
                  placeholder="Address Line 2"
                  variant="subtle"
                  h="10"
                  rounded="12px"
                  bg="#202020"
                  _focusVisible={{
                     border: 'none',
                     outline: 'none',
                     boxShadow: 'none',
                  }}
                  {...register('address_2')}
               />
               <Field.ErrorText>{errors.address_2?.message}</Field.ErrorText>
            </Field.Root>
            <Box css={signupStyles.double_input}>
               <Field.Root invalid={Boolean(errors.post_code?.message)}>
                  <Field.Label textStyle="xs" color="white" mb="1">
                     Post Code
                     <Box as="span" color="error">
                        *
                     </Box>
                  </Field.Label>
                  <Input
                     placeholder="Post Code"
                     variant="subtle"
                     h="10"
                     rounded="12px"
                     bg="#202020"
                     _focusVisible={{
                        border: 'none',
                        outline: 'none',
                        boxShadow: 'none',
                     }}
                     {...register('post_code')}
                  />
                  <Field.ErrorText>{errors.post_code?.message}</Field.ErrorText>
               </Field.Root>

               <Field.Root invalid={Boolean(errors.phone_number?.message)}>
                  <Field.Label textStyle="xs" color="white" mb="1">
                     Phone Number
                     <Box as="span" color="error">
                        *
                     </Box>
                  </Field.Label>
                  <PhoneInput
                     country={'gb'}
                     preferredCountries={['gb', 'us']}
                     placeholder="Phone Number"
                     enableSearch
                     value={phone_number}
                     onChange={v => setValue('phone_number', v)}
                     inputStyle={{
                        width: '100%',
                        height: '40px',
                        background: '#202020',
                        borderRadius: '12px',
                        border: errors.phone_number?.message ? '1px solid #f87171' : 'none',
                        color: 'white',
                        paddingLeft: '46px',
                        fontSize: '14px',
                     }}
                     dropdownStyle={{
                        backgroundColor: '#000',
                        color: 'white',
                     }}
                  />
                  <Field.ErrorText>{errors.phone_number?.message}</Field.ErrorText>
               </Field.Root>
            </Box>
            <Box css={signupStyles.double_input}>
               <Field.Root invalid={Boolean(errors.state?.message)}>
                  <Field.Label textStyle="xs" color="white" mb="1">
                     Town
                     <Box as="span" color="error">
                        *
                     </Box>
                  </Field.Label>
                  <Input
                     type="text"
                     css={signupStyles.num_input}
                     placeholder="Town"
                     variant="subtle"
                     h="10"
                     rounded="12px"
                     bg="#202020"
                     _focusVisible={{
                        border: 'none',
                        outline: 'none',
                        boxShadow: 'none',
                     }}
                     {...register('state')}
                  />
                  <Field.ErrorText>{errors.state?.message}</Field.ErrorText>
               </Field.Root>

               <Field.Root invalid={Boolean(errors.city?.message)}>
                  <Field.Label textStyle="xs" color="white" mb="1">
                     City
                     <Box as="span" color="error">
                        *
                     </Box>
                  </Field.Label>
                  <Input
                     type="text"
                     css={signupStyles.num_input}
                     placeholder="City"
                     variant="subtle"
                     h="10"
                     rounded="12px"
                     bg="#202020"
                     _focusVisible={{
                        border: 'none',
                        outline: 'none',
                        boxShadow: 'none',
                     }}
                     {...register('city')}
                  />
                  <Field.ErrorText>{errors.city?.message}</Field.ErrorText>
               </Field.Root>
            </Box>
            <Field.Root invalid={Boolean(errors.nationality?.message)}>
               <Field.Label textStyle="xs" color="white" mb="1">
                  Residence
                  <Box as="span" color="error">
                     *
                  </Box>
               </Field.Label>
               <Box
                  css={{
                     ...signupStyles.resident_input,
                     ...(errors.nationality?.message ? signupStyles.resident_input_error : {}),
                  }}
               >
                  <ReactFlagsSelect
                     selected={nationality}
                     onSelect={code => setValue('nationality', code)}
                     searchable
                     fullWidth
                     countries={['GB']}
                  />
               </Box>

               <Field.ErrorText>{errors.nationality?.message}</Field.ErrorText>
            </Field.Root>
            <Field.Root invalid={Boolean(errors.website?.message)}>
               <Field.Label textStyle="xs" color="white" mb="1">
                  Partner website Link
                  <Box as="span" color="error">
                     *
                  </Box>
               </Field.Label>
               <Input
                  placeholder="Website must start with https://"
                  variant="subtle"
                  h="10"
                  rounded="12px"
                  bg="#202020"
                  _focusVisible={{
                     border: 'none',
                     outline: 'none',
                     boxShadow: 'none',
                  }}
                  {...register('website')}
               />
               <Field.ErrorText>{errors.website?.message}</Field.ErrorText>
            </Field.Root>
            <Text textStyle="xs" color="white" mb="0">
               Social Media Link
               <Box as="span" color="error" ml={1}>
                  *
               </Box>
            </Text>
            {fields.map((field, index) => (
               <Box key={field.id} display="flex" alignItems="center" gap="2" mb="3" w="full">
                  {/* URL Field */}
                  <Box h="10" rounded="12px" bg="#202020" w="full" tabIndex={0}>
                     <InputGroup
                        endElement={
                           <Select.Root
                              collection={socialMedia}
                              size="sm"
                              width={{ base: '120px', md: '165px' }}
                              {...register(`social_media.${index}.platform`)}
                           >
                              <Select.HiddenSelect />
                              <Select.Control>
                                 <Select.Trigger border="none">
                                    <Select.ValueText fontSize="12px" placeholder="Select Social Media" />
                                 </Select.Trigger>
                                 <Select.IndicatorGroup>
                                    <Select.Indicator />
                                 </Select.IndicatorGroup>
                              </Select.Control>
                              <Portal>
                                 <Select.Positioner>
                                    <Select.Content>
                                       {socialMedia.items.map(item => (
                                          <Select.Item item={item} key={item.value}>
                                             {item.label}
                                             <Select.ItemIndicator />
                                          </Select.Item>
                                       ))}
                                    </Select.Content>
                                 </Select.Positioner>
                              </Portal>
                           </Select.Root>
                        }
                     >
                        <Field.Root flex="1" invalid={Boolean(errors.social_media?.[index]?.url)}>
                           <Input
                              placeholder="Website must start with https://"
                              variant="subtle"
                              w={{ base: '150px', md: '200px' }}
                              bg="transparent"
                              _focusVisible={{
                                 border: 'none',
                                 outline: 'none',
                                 boxShadow: 'none',
                              }}
                              {...register(`social_media.${index}.url`)}
                           />
                           <Field.ErrorText>{errors.social_media?.[index]?.url?.message}</Field.ErrorText>
                           <Field.ErrorText>{errors.social_media?.[index]?.platform?.message}</Field.ErrorText>
                        </Field.Root>
                     </InputGroup>
                  </Box>

                  {/* Social Media Select */}

                  {fields.length - 1 === index ? (
                     <IconButton
                        marginTop="auto"
                        w={10}
                        h={10}
                        type="button"
                        aria-label="Add Social Media"
                        onClick={() => append({ platform: '', url: '' })}
                        rounded="full"
                        bg="#252312"
                        color="white"
                        _hover={{ bg: '#3a3a3a' }}
                     >
                        <FaPlus color="#F5E876" />
                     </IconButton>
                  ) : (
                     <IconButton
                        marginTop="auto"
                        w={10}
                        h={10}
                        type="button"
                        aria-label="Remove Social Media"
                        onClick={() => remove(index)}
                        rounded="full"
                        bg="#260E0E"
                        color="white"
                        _hover={{ bg: '#3a3a3a' }}
                     >
                        <FaMinus color="red" />
                     </IconButton>
                  )}
               </Box>
            ))}

            <Box css={signupStyles.signup_btn_con}>
               <Button
                  type="reset"
                  onClick={() => reset()}
                  bg="darkerBtn"
                  textStyle="xs"
                  color="white"
                  h="10"
                  maxW="36"
                  minW="36"
                  p="0"
               >
                  Reset
               </Button>
               <Button type="submit" textStyle="xs" h="10" maxW="36" minW="36" p="0">
                  Next
               </Button>
            </Box>
         </Stack>
      </Box>
   );
};

export default PartnerDetailsForm;
